import React from 'react';
import { useSelector } from 'react-redux';
import { NavLink, Route, useParams, useRouteMatch } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { RootState } from '../../../core/state/state';
import { UserRoleType, RoleType } from '../../../core/state/user/types';
import Button from '../../../components/form/button/Button';
import Icon from '../../../components/icon/Icon';
import { Icon as IconObject } from '../../../core/constants/Icon';
import Tooltip from '../../../components/tooltip/Tooltip';
import {
  EXTERNAL_USERS,
  EXTERNAL_USER_CONTACTS,
  EXTERNAL_USER_PROJECTS,
  HOME_ROUTE,
  INTERNAL_USERS,
  INTERNAL_USER_PROJECTS,
  PROJECT_BOOKINGS,
  PROJECT_DOCUMENTS,
  NOTIFICATIONS,
  PROJECT_MANAGEMENT,
  PROJECT_ROLES_EXTERNAL,
  PROJECT_ROLES_INTERNAL,
  PROJECT_SETTINGS,
  COMPANY_CONTACTS,
  PROFILE,
  BARRIER_MANAGEMENT,
  NOTIFICATIONS_EMAIL,
  BARRIER_LOGS
} from '../../../core/constants/routes';
import { showSidebarLink } from '../../../core/utils/showSidebarLink';
import { useInternalUser } from '../../../core/hooks/useInternalUser';

import './Sidebar.scss';

interface Props {
  isCollapsed: boolean;
  onCollapse: () => void;
}

const Sidebar: React.FC<Props> = ({ onCollapse, isCollapsed }) => {
  const { t } = useTranslation();
  const { path } = useRouteMatch<{ path: string }>();
  const profileAndCompanyContactsPageMatch = useRouteMatch([PROFILE, COMPANY_CONTACTS]);
  const { projectId, userId } = useParams<{ projectId: string; userId: string }>();
  const subcontractorProjectId = useSelector((state: RootState) => state.user.userInfo?.projectID);
  const userRoles = useSelector((state: RootState) => state.user.userInfo?.userRole);
  const userProjectRoles = useSelector((state: RootState) => state.projects.projectDetails?.userRole);
  const isInternal = useInternalUser();

  const getSidebarLinks = () => {
    switch (path) {
      case PROJECT_BOOKINGS:
      case PROJECT_DOCUMENTS:
      case PROJECT_ROLES_INTERNAL:
      case PROJECT_ROLES_EXTERNAL:
      case PROJECT_SETTINGS:
        return [
          {
            name: 'sidebar.link.booking',
            path: '/projects/:projectId/bookings',
            icon: 'calendar',
            link: `/projects/${projectId}/bookings`,
            roles: [
              UserRoleType.SystemAdmin,
              UserRoleType.ReadAll,
              UserRoleType.ProjectAdmin,
              UserRoleType.ProjectParticipant,
              UserRoleType.Subcontractor
            ],
            roleType: RoleType.ProjectRole
          },
          {
            name: 'sidebar.link.internalRoles',
            path: '/projects/:projectId/roles/internal',
            icon: 'roles',
            link: `/projects/${projectId}/roles/internal`,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ProjectAdmin],
            roleType: RoleType.ProjectRole
          },
          {
            name: 'sidebar.link.externalRoles',
            path: '/projects/:projectId/roles/external',
            icon: 'company',
            link: `/projects/${projectId}/roles/external`,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ProjectAdmin],
            roleType: RoleType.ProjectRole
          },
          {
            name: 'sidebar.link.documents',
            path: '/projects/:projectId/documents',
            icon: 'files',
            link: `/projects/${projectId}/documents`,
            roles: [
              UserRoleType.SystemAdmin,
              UserRoleType.ReadAll,
              UserRoleType.ProjectAdmin,
              UserRoleType.ProjectParticipant,
              UserRoleType.Subcontractor
            ],
            roleType: RoleType.ProjectRole
          },
          {
            name: 'sidebar.link.settings',
            path: '/projects/:projectId/settings',
            icon: 'settings',
            link: `/projects/${projectId}/settings`,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ProjectAdmin],
            roleType: RoleType.ProjectRole
          }
        ];
      case HOME_ROUTE:
      case INTERNAL_USERS:
      case EXTERNAL_USERS:
      case NOTIFICATIONS:
      case PROJECT_MANAGEMENT:
      case BARRIER_MANAGEMENT:
        return [
          {
            name: 'sidebar.link.allProjects',
            path: '/',
            icon: 'projectsDark',
            link: `/`,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ReadAll, UserRoleType.ProjectAdmin, UserRoleType.ProjectParticipant],
            roleType: RoleType.SystemRole
          },
          {
            name: 'sidebar.link.internalUsers',
            path: '/internal-users',
            icon: 'profile',
            link: `/internal-users`,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ReadAll],
            roleType: RoleType.SystemRole
          },
          {
            name: 'sidebar.link.externalUsers',
            path: '/external-users',
            icon: 'company',
            link: `/external-users`,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ReadAll],
            roleType: RoleType.SystemRole
          },
          {
            name: 'sidebar.link.projectManagement',
            path: '/project-management',
            icon: 'globalSettings',
            link: `/project-management`,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ReadAll],
            roleType: RoleType.SystemRole
          },
          {
            name: 'sidebar.link.barrierManagement',
            path: '/barrier-management',
            icon: 'barrierSystem',
            link: `/barrier-management`,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ReadAll],
            roleType: RoleType.SystemRole
          }
        ];
      case INTERNAL_USER_PROJECTS:
        return [
          {
            name: 'sidebar.link.internalUser.projects',
            path: '/internal-users/:userId/projects',
            icon: 'projectsDark',
            link: `/internal-users/${userId}/projects`,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ReadAll],
            roleType: RoleType.SystemRole
          }
        ];
      case EXTERNAL_USER_CONTACTS:
      case EXTERNAL_USER_PROJECTS:
        return [
          {
            name: 'sidebar.link.externalUser.contacts',
            path: '/external-users/:userId/contacts',
            icon: 'roles',
            link: `/external-users/${userId}/contacts`,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ReadAll],
            roleType: RoleType.SystemRole
          },
          {
            name: 'sidebar.link.externalUser.projects',
            path: '/external-users/:userId/projects',
            icon: 'projectsDark',
            link: `/external-users/${userId}/projects`,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ReadAll],
            roleType: RoleType.SystemRole
          }
        ];
      case PROFILE:
      case COMPANY_CONTACTS:
      case NOTIFICATIONS_EMAIL:
        return [
          {
            name: 'sidebar.link.generalInfo',
            path: '/profile',
            icon: 'company',
            link: '/profile',
            roles: [UserRoleType.Subcontractor],
            roleType: RoleType.SystemRole
          },
          {
            name: 'sidebar.link.externalUser.contacts',
            path: '/company-contacts',
            icon: 'roles',
            link: '/company-contacts',
            roles: [UserRoleType.Subcontractor],
            roleType: RoleType.SystemRole
          },
          {
            name: 'sidebar.link.notificationsEmails',
            path: '/notification-email',
            icon: 'bell',
            link: '/notification-email',
            roles: [UserRoleType.Subcontractor],
            roleType: RoleType.SystemRole
          }
        ];
      case BARRIER_LOGS:
        return [
          {
            name: 'sidebar.link.barrierManagementLogs',
            path: BARRIER_LOGS,
            icon: 'barrierSystem',
            link: BARRIER_LOGS,
            roles: [UserRoleType.SystemAdmin, UserRoleType.ReadAll],
            roleType: RoleType.SystemRole
          }
        ];
      default:
        return null;
    }
  };

  const renderHomeRoute = () => (profileAndCompanyContactsPageMatch ? `/projects/${subcontractorProjectId}/bookings` : HOME_ROUTE);

  const renderBackToRoute = (route: string | null, sidebarText: string) => {
    return (
      <NavLink key="back" className="sidebar__link sidebar__link--small" to={!route ? renderHomeRoute() : route}>
        <div className="sidebar__back">
          <Icon className="sidebar__arrow sidebar__arrow--left" icon="arrow" size="small" svg />
        </div>
        {!isCollapsed && <span className="sidebar__link-text">{t(sidebarText)}</span>}
      </NavLink>
    );
  };

  return (
    <div className="sidebar">
      {isInternal && (
        <Route
          path={[PROJECT_BOOKINGS, PROJECT_ROLES_INTERNAL, PROJECT_ROLES_EXTERNAL, PROJECT_DOCUMENTS, PROJECT_SETTINGS, PROFILE, COMPANY_CONTACTS]}
        >
          {renderBackToRoute(null, 'sidebar.link.back')}
        </Route>
      )}
      {path === BARRIER_LOGS && <Route path={BARRIER_LOGS}>{renderBackToRoute(BARRIER_MANAGEMENT, 'sidebar.link.barrierManagementBack')}</Route>}
      <Route path={[INTERNAL_USER_PROJECTS]}>{renderBackToRoute(INTERNAL_USERS, 'sidebar.link.backToInternalUsers')}</Route>
      <Route path={[EXTERNAL_USER_CONTACTS, EXTERNAL_USER_PROJECTS]}>{renderBackToRoute(EXTERNAL_USERS, 'sidebar.link.backToExternalUsers')}</Route>
      {getSidebarLinks()?.map((item) => {
        if (userRoles && showSidebarLink(userRoles, item.roles, item.roleType, userProjectRoles)) {
          return (
            <NavLink key={`key-${item.path}`} className={`sidebar__link ${path === item.path ? 'sidebar__link--active' : ''}`} to={item.link}>
              <Icon icon={item.icon as keyof typeof IconObject} size="small" svg />
              {!isCollapsed && <span className="sidebar__link-text">{t(`${item.name}`)}</span>}
            </NavLink>
          );
        }
        return null;
      })}
      <Button className="sidebar__button" buttonTypes={['extra-small', 'white']} onClick={onCollapse}>
        <Tooltip text={isCollapsed ? t('sidebar.toogle.tooltip.collapsed') : t('sidebar.toogle.tooltip.expanded')}>
          <Icon size="small" icon="arrow" className={`sidebar__arrow ${isCollapsed ? 'sidebar__arrow--right' : ''}`} svg />
        </Tooltip>
      </Button>
    </div>
  );
};

export default Sidebar;
